import { useRef, useState } from 'react';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Disc3, Download, FileCog, Plus, Trash2, Usb } from 'lucide-react';
import { format } from 'date-fns';
import { config } from '../../config';
import { apiErrorMessage } from '@/utils/apiError';
import {
  type ProvisioningToken,
  SETUP_FILE_TOKEN_DAYS,
  SETUP_FILE_TOKEN_DEFAULT_DAYS,
  useCreateProvisioningToken,
} from '@/hooks/useProvisioningTokens';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type EthernetForm,
  type InterfaceForm,
  type RouteForm,
  type SetupFileResponse,
  type SetupForm,
  type VlanForm,
  blankEthernet,
  blankVlan,
  initialForm,
  interfaceNames,
  newKey,
  problemsOf,
  saveFile,
  toSettings,
  vlanName,
} from './setupFile';

const SELECT = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm';

/**
 * A setup file for the appliance: its network settings on a USB drive, or on an
 * ISO a VM attaches (docs/GatewaySetupFile.md). Witina signs it for this
 * gateway only, and the gateway checks that; nothing is stored, so each
 * download is a new file.
 */
export function SetupFileCard({ gatewayId, gatewayName }: { gatewayId: string; gatewayName: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-border-subtle bg-card p-3 space-y-2" data-testid="setup-file">
      <div className="flex items-center gap-2 text-sm font-medium">
        <FileCog className="w-4 h-4 text-muted-foreground" />
        Setup file
      </div>
      <p className="text-xs text-muted-foreground">
        Static addresses, VLANs, DNS and more, applied from a USB drive or an ISO attached to the VM, when the gateway
        can&apos;t be reached any other way.
      </p>
      <Button size="sm" variant="outline" onClick={() => { setOpen(true); }}>
        Make a setup file
      </Button>
      {open ? (
        <SetupFileDialog target={{ gatewayId, gatewayName }} onClose={() => { setOpen(false); }} />
      ) : null}
    </div>
  );
}

/** Where Add gateway puts the gateways a new-gateway file adds. */
export interface NewGateways {
  organizationId: string;
  regionId: string;
  divisionId: string | null;
  name: string | null;
  description: string | null;
}

/**
 * Who a file is for: a claimed gateway, which alone takes it; or new gateways
 * (slice 5), which take it while unclaimed and are claimed by the provisioning
 * token it carries. That token is made for the file, on its first download,
 * and adds any number of gateways until it expires.
 */
export type SetupFileTarget =
  | { gatewayId: string; gatewayName: string }
  | { newGateways: NewGateways; token?: ProvisioningToken; onToken: (token: ProvisioningToken) => void };

export function SetupFileDialog({ target, onClose }: { target: SetupFileTarget; onClose: () => void }) {
  const [form, setForm] = useState<SetupForm>(initialForm);
  const [problems, setProblems] = useState<string[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [files, setFiles] = useState<SetupFileResponse | undefined>(undefined);
  const [days, setDays] = useState<number>(SETUP_FILE_TOKEN_DEFAULT_DAYS);
  const forNew = 'newGateways' in target ? target : undefined;
  // Reopened, the form makes files with the token it made before.
  const [fileToken, setFileToken] = useState<ProvisioningToken | undefined>(forNew?.token);
  const createToken = useCreateProvisioningToken();

  // The token, once: the pair and the ISO carry the same one. Its lifetime is
  // fixed from then on; cancelling it is always an explicit act.
  const mintUrl = async () => {
    if ('gatewayId' in target) return `${config.backendApi}/api/gateways/${target.gatewayId}/setup-file`;
    let token = fileToken;
    if (!token) {
      token = await createToken.mutateAsync({
        ...target.newGateways,
        gateway_type: 'appliance',
        unlimitedUses: true,
        expiresInDays: days,
      });
      setFileToken(token);
      target.onToken(token);
    }
    return `${config.backendApi}/api/gateways/tokens/${token.id}/setup-file`;
  };

  const update = (change: Partial<SetupForm>) => {
    setForm((f) => ({ ...f, ...change }));
    setFiles(undefined);
  };

  const caFileRef = useRef<HTMLInputElement>(null);
  const loadCa = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) update({ proxyCa: await file.text() });
  };

  const failed = async (e: unknown, fallback: string) => {
    const body: unknown = axios.isAxiosError(e) ? e.response?.data : undefined;
    // An ISO request asks for a blob, so its 422 arrives as one.
    const parsed = body instanceof Blob ? (JSON.parse(await body.text()) as unknown) : body;
    const listed = problemsOf(parsed);
    setProblems(listed ?? []);
    setError(listed ? undefined : apiErrorMessage(e, fallback));
  };

  const mintFiles = useMutation({
    mutationFn: async () =>
      (await axios.post<SetupFileResponse>(await mintUrl(), toSettings(form), { withCredentials: true })).data,
    onSuccess: (result) => {
      setProblems([]);
      setError(undefined);
      setFiles(result);
    },
    onError: (e) => { void failed(e, 'Could not make the setup file.'); },
  });

  const mintIso = useMutation({
    mutationFn: async () =>
      await axios.post<Blob>(`${await mintUrl()}/iso`, toSettings(form), { withCredentials: true, responseType: 'blob' }),
    onSuccess: (response) => {
      setProblems([]);
      setError(undefined);
      const disposition = String(response.headers['content-disposition'] ?? '');
      const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'witina-setup.iso';
      saveFile(name, response.data);
    },
    onError: (e) => { void failed(e, 'Could not make the setup ISO.'); },
  });

  const busy = mintFiles.isPending || mintIso.isPending;
  const names = interfaceNames(form);
  const ports = form.ethernets.map((e) => e.name.trim()).filter((n) => n !== '');

  const setEthernet = (key: string, change: Partial<EthernetForm>) => {
    update({ ethernets: form.ethernets.map((e) => (e.key === key ? { ...e, ...change } : e)) });
  };
  const setVlan = (key: string, change: Partial<VlanForm>) => {
    update({ vlans: form.vlans.map((v) => (v.key === key ? { ...v, ...change } : v)) });
  };
  const setRoute = (key: string, change: Partial<RouteForm>) => {
    update({ routes: form.routes.map((r) => (r.key === key ? { ...r, ...change } : r)) });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{'gatewayId' in target ? `Setup file for ${target.gatewayName}` : 'Setup file for new gateways'}</DialogTitle>
          <DialogDescription>
            {forNew
              ? 'Only what you fill in is set: a port or VLAN you add gets exactly these settings, and anything you leave out stays as the gateway has it. A new gateway takes the file until it is claimed, and the token in it claims it.'
              : 'Only what you fill in is changed: a port or VLAN you add gets exactly these settings, and anything you leave out stays as it is on the gateway. Witina signs the file for this gateway, so no other gateway will take it.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {forNew ? (
            <section className="space-y-1" data-testid="sf-claiming">
              <h3 className="text-sm font-medium">Claiming</h3>
              {fileToken ? (
                <p className="text-xs text-muted-foreground">
                  Its token adds every gateway the file goes on until {until(fileToken.expiresAt)}. To stop it sooner,
                  cancel it under Provisioning tokens on the Gateways page.
                </p>
              ) : (
                <>
                  <div className="max-w-sm space-y-1">
                    <Label htmlFor="sf-token-days">Adds gateways for</Label>
                    <select id="sf-token-days" className={SELECT} value={days}
                      onChange={(e) => { setDays(Number(e.target.value)); }}>
                      {SETUP_FILE_TOKEN_DAYS.map((d) => (
                        <option key={d} value={d}>{d === 1 ? '1 day' : `${String(d)} days`}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Any number of gateways, while it lasts: one drive or ISO for a whole rollout. Every gateway that
                    uses it gets the same settings, so give static addresses only where each site&apos;s are the same.
                  </p>
                </>
              )}
            </section>
          ) : null}

          <div className="space-y-1 max-w-sm">
            <Label htmlFor="sf-hostname">Host name</Label>
            <Input id="sf-hostname" value={form.hostname} placeholder="Leave as it is"
              onChange={(e) => { update({ hostname: e.target.value }); }} />
          </div>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">Ports</h3>
            {form.ethernets.length === 0 ? (
              <p className="text-xs text-muted-foreground">None: the gateway&apos;s ports stay as they are.</p>
            ) : null}
            {form.ethernets.map((e, n) => (
              <div key={e.key} className="rounded-md border border-border-subtle p-3 space-y-3" data-testid="sf-ethernet">
                <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end">
                  <div className="space-y-1">
                    <Label htmlFor={`sf-eth-name-${e.key}`}>Port</Label>
                    <Input id={`sf-eth-name-${e.key}`} value={e.name} placeholder={`eth${String(n)}`}
                      onChange={(ev) => { setEthernet(e.key, { name: ev.target.value }); }} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`sf-eth-mac-${e.key}`}>Match by MAC (optional)</Label>
                    <Input id={`sf-eth-mac-${e.key}`} value={e.macaddress} placeholder="00:e0:67:2a:11:03"
                      onChange={(ev) => { setEthernet(e.key, { macaddress: ev.target.value }); }} />
                  </div>
                  <Button variant="ghost" size="sm" aria-label={`Remove port ${e.name || String(n + 1)}`}
                    onClick={() => { update({ ethernets: form.ethernets.filter((x) => x.key !== e.key), uplink: form.uplink === e.name.trim() ? '' : form.uplink }); }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ports are eth0, eth1… in the gateway&apos;s own order. With a MAC address, the port is the one with
                  that address, under any name you choose.
                </p>
                <InterfaceFields idPrefix={`sf-eth-${e.key}`} value={e} onChange={(c) => { setEthernet(e.key, c); }} />
              </div>
            ))}
            <Button size="sm" variant="outline" className="gap-1"
              onClick={() => { update({ ethernets: [...form.ethernets, blankEthernet(`eth${String(form.ethernets.length)}`)] }); }}>
              <Plus className="w-4 h-4" /> Add port
            </Button>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">VLANs</h3>
            {form.vlans.map((v) => (
              <div key={v.key} className="rounded-md border border-border-subtle p-3 space-y-3" data-testid="sf-vlan">
                <div className="grid gap-3 sm:grid-cols-[1fr_8rem_1fr_auto] items-end">
                  <div className="space-y-1">
                    <Label htmlFor={`sf-vlan-link-${v.key}`}>On port</Label>
                    <select id={`sf-vlan-link-${v.key}`} className={SELECT} value={v.link}
                      onChange={(ev) => { setVlan(v.key, { link: ev.target.value }); }}>
                      <option value="">Choose a port</option>
                      {ports.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`sf-vlan-id-${v.key}`}>VLAN ID</Label>
                    <Input id={`sf-vlan-id-${v.key}`} value={v.id} inputMode="numeric" placeholder="120"
                      onChange={(ev) => { setVlan(v.key, { id: ev.target.value }); }} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`sf-vlan-name-${v.key}`}>Name (optional)</Label>
                    <Input id={`sf-vlan-name-${v.key}`} value={v.name} placeholder={vlanName(v)}
                      onChange={(ev) => { setVlan(v.key, { name: ev.target.value }); }} />
                  </div>
                  <Button variant="ghost" size="sm" aria-label={`Remove VLAN ${vlanName(v)}`}
                    onClick={() => { update({ vlans: form.vlans.filter((x) => x.key !== v.key) }); }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <InterfaceFields idPrefix={`sf-vlan-${v.key}`} value={v} onChange={(c) => { setVlan(v.key, c); }} />
              </div>
            ))}
            <Button size="sm" variant="outline" className="gap-1" disabled={ports.length === 0}
              onClick={() => { update({ vlans: [...form.vlans, blankVlan(ports[0] ?? '')] }); }}>
              <Plus className="w-4 h-4" /> Add VLAN
            </Button>
            {ports.length === 0 ? <p className="text-xs text-muted-foreground">Add the port a VLAN is on first.</p> : null}
          </section>

          <div className="space-y-1 max-w-sm">
            <Label htmlFor="sf-uplink">Uplink</Label>
            <select id="sf-uplink" className={SELECT} value={form.uplink}
              onChange={(e) => { update({ uplink: e.target.value }); }}>
              <option value="">None chosen</option>
              {names.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
            <p className="text-xs text-muted-foreground">
              The interface the gateway reaches Witina through. It alone gets a default route, and the console shows its
              address.
            </p>
          </div>

          <section className="space-y-3">
            <h3 className="text-sm font-medium">DNS, NTP and routes</h3>
            <p className="text-xs text-muted-foreground">Each one you turn on replaces the gateway&apos;s whole list.</p>
            <ListOption id="sf-dns" label="Set DNS servers" checked={form.setNameservers} value={form.nameservers}
              placeholder="10.20.0.10, 10.20.0.11"
              note="Replaces the servers DHCP hands out too."
              onToggle={(on) => { update({ setNameservers: on }); }}
              onChange={(text) => { update({ nameservers: text }); }} />
            <ListOption id="sf-search" label="Set search domains" checked={form.setSearch} value={form.search}
              placeholder="acme.local" onToggle={(on) => { update({ setSearch: on }); }}
              onChange={(text) => { update({ search: text }); }} />
            <ListOption id="sf-ntp" label="Set NTP servers" checked={form.setNtp} value={form.ntp}
              placeholder="ntp.acme.local" onToggle={(on) => { update({ setNtp: on }); }}
              onChange={(text) => { update({ ntp: text }); }} />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="sf-routes" checked={form.setRoutes}
                  onCheckedChange={(c) => { update({ setRoutes: c === true }); }} />
                <Label htmlFor="sf-routes">Replace static routes</Label>
              </div>
              {form.setRoutes ? (
                <div className="space-y-2 pl-6">
                  <p className="text-xs text-muted-foreground">
                    Every static route but the default one. The default route is the uplink&apos;s gateway.
                  </p>
                  {form.routes.map((r) => (
                    <div key={r.key} className="grid gap-2 grid-cols-[1fr_1fr_auto]" data-testid="sf-route">
                      <Input aria-label="Network" value={r.to} placeholder="10.99.0.0/16"
                        onChange={(e) => { setRoute(r.key, { to: e.target.value }); }} />
                      <Input aria-label="Via" value={r.via} placeholder="10.20.0.254"
                        onChange={(e) => { setRoute(r.key, { via: e.target.value }); }} />
                      <Button variant="ghost" size="sm" aria-label="Remove route"
                        onClick={() => { update({ routes: form.routes.filter((x) => x.key !== r.key) }); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="gap-1"
                    onClick={() => { update({ routes: [...form.routes, { key: newKey(), to: '', via: '' }] }); }}>
                    <Plus className="w-4 h-4" /> Add route
                  </Button>
                </div>
              ) : null}
            </div>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-medium">Proxy</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="sf-proxy" checked={form.setProxy}
                  onCheckedChange={(c) => { update({ setProxy: c === true }); }} />
                <Label htmlFor="sf-proxy">Set the proxy</Label>
              </div>
              {form.setProxy ? (
                <div className="space-y-2 pl-6">
                  <Input aria-label="Proxy address" value={form.proxyAddress} placeholder="proxy.acme.local:3128"
                    onChange={(e) => { update({ proxyAddress: e.target.value }); }} />
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input aria-label="Proxy user name" value={form.proxyUser} placeholder="User name (optional)"
                      autoComplete="off" onChange={(e) => { update({ proxyUser: e.target.value }); }} />
                    <Input aria-label="Proxy password" type="password" value={form.proxyPassword}
                      placeholder="Password (optional)" autoComplete="new-password"
                      onChange={(e) => { update({ proxyPassword: e.target.value }); }} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The gateway reaches Witina through it (HTTP CONNECT); its devices, never. Give the port. Leave the
                    address empty for no proxy: the gateway then connects directly. The password is in the file, so
                    keep the drive safe.
                  </p>
                  <div className="flex items-center justify-between gap-2 pt-2">
                    <Label htmlFor="sf-proxy-ca">CA certificates (PEM)</Label>
                    <Button size="sm" variant="outline" onClick={() => { caFileRef.current?.click(); }}>
                      Load from a file
                    </Button>
                    <input ref={caFileRef} type="file" accept=".pem,.crt,.cer,.txt" className="hidden"
                      data-testid="sf-proxy-ca-file" onChange={(e) => { void loadCa(e); }} />
                  </div>
                  <Textarea id="sf-proxy-ca" value={form.proxyCa} rows={4} spellCheck={false}
                    className="font-mono text-xs" placeholder="-----BEGIN CERTIFICATE-----"
                    onChange={(e) => { update({ proxyCa: e.target.value }); }} />
                  <p className="text-xs text-muted-foreground">
                    Only for a proxy or firewall that inspects TLS: the gateway trusts these CAs, beside the public
                    ones, for its connection to Witina and nothing else. Whoever holds their keys can read that
                    connection. A firewall needs no address above.
                  </p>
                </div>
              ) : null}
            </div>
          </section>

          {problems.length > 0 ? (
            <Alert variant="destructive" data-testid="sf-problems">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <p>Fix {problems.length === 1 ? 'this' : `these ${String(problems.length)}`} first:</p>
                <ul className="list-disc pl-5">
                  {problems.map((p) => <li key={p}>{p}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          {files ? (
            <div className="rounded-md border border-border-subtle bg-muted/30 p-3 space-y-2" data-testid="sf-files">
              <p className="text-sm">
                Copy both files to the root of a USB drive, and plug it into the gateway. Made {files.issuedAt}: the
                gateway won&apos;t take a file older than the last one it applied.
              </p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: files.fileName, text: files.yaml },
                  { name: files.signatureName, text: files.signature },
                ].map((f) => (
                  <Button key={f.name} size="sm" variant="outline" className="gap-1 font-mono"
                    onClick={() => { saveFile(f.name, new Blob([f.text], { type: 'text/plain' })); }}>
                    <Download className="w-4 h-4" /> {f.name}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" className="gap-1" disabled={busy} onClick={() => { mintIso.mutate(); }}>
            <Disc3 className="w-4 h-4" /> ISO for a VM
          </Button>
          <Button className="gap-1" disabled={busy} onClick={() => { mintFiles.mutate(); }}>
            <Usb className="w-4 h-4" /> Files for a USB drive
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** When a token expires, as the server said it. */
function until(expiresAt: string): string {
  const at = new Date(expiresAt);
  return isNaN(at.getTime()) ? expiresAt : format(at, 'PPp');
}

function InterfaceFields({ idPrefix, value, onChange }: {
  idPrefix: string;
  value: InterfaceForm;
  onChange: (change: Partial<InterfaceForm>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-addresses`}>Static addresses</Label>
        <Input id={`${idPrefix}-addresses`} value={value.addresses} placeholder="10.20.0.57/24, fd00:20::57/64"
          onChange={(e) => { onChange({ addresses: e.target.value }); }} />
      </div>
      <div className="flex flex-wrap gap-4">
        {([
          ['dhcp4', 'DHCP'],
          ['dhcp6', 'DHCPv6'],
          ['acceptRa', 'IPv6 SLAAC'],
        ] as const).map(([key, label]) => (
          <div key={key} className="flex items-center gap-2">
            <Checkbox id={`${idPrefix}-${key}`} checked={value[key]}
              onCheckedChange={(c) => { onChange({ [key]: c === true }); }} />
            <Label htmlFor={`${idPrefix}-${key}`}>{label}</Label>
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-gateway4`}>IPv4 gateway</Label>
          <Input id={`${idPrefix}-gateway4`} value={value.gateway4} placeholder="10.20.0.1"
            onChange={(e) => { onChange({ gateway4: e.target.value }); }} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-gateway6`}>IPv6 gateway</Label>
          <Input id={`${idPrefix}-gateway6`} value={value.gateway6} placeholder="fe80::1"
            onChange={(e) => { onChange({ gateway6: e.target.value }); }} />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${idPrefix}-mtu`}>MTU</Label>
          <Input id={`${idPrefix}-mtu`} value={value.mtu} inputMode="numeric" placeholder="1500"
            onChange={(e) => { onChange({ mtu: e.target.value }); }} />
        </div>
      </div>
    </div>
  );
}

function ListOption({ id, label, checked, value, placeholder, note, onToggle, onChange }: {
  id: string;
  label: string;
  checked: boolean;
  value: string;
  placeholder: string;
  note?: string;
  onToggle: (on: boolean) => void;
  onChange: (text: string) => void;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Checkbox id={id} checked={checked} onCheckedChange={(c) => { onToggle(c === true); }} />
        <Label htmlFor={id}>{label}</Label>
      </div>
      {checked ? (
        <div className="pl-6 space-y-1">
          <Input aria-label={label} value={value} placeholder={placeholder}
            onChange={(e) => { onChange(e.target.value); }} />
          {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
        </div>
      ) : null}
    </div>
  );
}
